import React, { useEffect, useState } from 'react'
import { useLocation } from 'react-router-dom'
import type { UserTrip } from '../api/profile/userProfileApi'
import {
  findPendingReviewTrip,
  TRIP_REVIEW_TRIGGER_EVENT,
} from '../utils/tripReview'
import TripReviewModal from './TripReviewModal'

export const TripReviewAutoPrompt: React.FC = () => {
  const [activeTrip, setActiveTrip] = useState<UserTrip | null>(null)
  const [isOpen, setIsOpen] = useState(false)
  const location = useLocation()

  // 1. Listen for manual review triggers (e.g. from MyTrips page)
  useEffect(() => {
    const handleManualTrigger = (e: Event) => {
      const customEvent = e as CustomEvent<{ trip: UserTrip }>
      if (customEvent.detail?.trip) {
        setActiveTrip(customEvent.detail.trip)
        setIsOpen(true)
      }
    }

    window.addEventListener(TRIP_REVIEW_TRIGGER_EVENT, handleManualTrigger)
    return () => {
      window.removeEventListener(TRIP_REVIEW_TRIGGER_EVENT, handleManualTrigger)
    }
  }, [])

  // 2. Auto-prompt check when visiting the site
  useEffect(() => {
    // Only authenticated users should ever be checked for trip reviews
    const token = typeof window !== 'undefined' ? localStorage.getItem('auth_token') : null
    if (!token) return

    // Avoid interrupting users during active booking checkout or admin login
    if (
      location.pathname.startsWith('/booking') ||
      location.pathname.startsWith('/admin')
    ) {
      return
    }

    let isCancelled = false

    const checkPendingTrip = async () => {
      const pending = await findPendingReviewTrip()
      if (pending && !isCancelled) {
        // Small graceful delay so page mounts smoothly before popping up
        const timer = setTimeout(() => {
          if (!isCancelled) {
            setActiveTrip(pending)
            setIsOpen(true)
          }
        }, 1400)

        return () => clearTimeout(timer)
      }
    }

    checkPendingTrip()

    return () => {
      isCancelled = true
    }
  }, [location.pathname])

  if (!activeTrip) return null

  return (
    <TripReviewModal
      trip={activeTrip}
      isOpen={isOpen}
      onClose={() => setIsOpen(false)}
      onSuccess={() => {
        // Refresh check or state
      }}
    />
  )
}

export default TripReviewAutoPrompt
